import * as React from 'react';
import { IResourceEntityPermissions, ResourceEntityAccessLevels } from "../../store/permissions";
import { Checkbox } from 'office-ui-fabric-react';
import { connect } from 'react-redux';
import Collapsible from '../common/Collapsible';
import { PPMFeatures, Subscription } from '../../store/Tenant';
import { ApplicationState } from '../../store';
import { ResourceEntityAccessLevel } from './ResourceEntityAccessLevel/ResourceEntityAccessLevel';
import InfoableComponent from '../common/InfoableComponent';
import Link from '../common/Link';

type StateProps = {
    subscription: Subscription;
}

interface OwnProps {
    readOnly?: boolean;
    expanded?: boolean;
    isAdministrative: boolean;
    perms: IResourceEntityPermissions;
    onChange: (changes: IResourceEntityPermissions) => void;
    indeterminate?: (level?: ResourceEntityAccessLevels) => boolean | undefined;
}

type Props = OwnProps & StateProps;

const ResourceEntityPermssions = (props: Props) => {
    const { expanded, readOnly, subscription, perms, isAdministrative } = props;
    const hasRolesFeature = Subscription.contains(subscription, PPMFeatures.ResourceRoles);

    const _onCheckChange = (perm: Partial<IResourceEntityPermissions>) => {
        const newPerm = { ...perms, ...perm };
        if (newPerm.resourceEntity === ResourceEntityAccessLevels.All) {
            newPerm.resourcePlan = ResourceEntityAccessLevels.All
        }
        props.onChange(newPerm);
    }

    return <Collapsible iconName="PPMXResource" title="Resources" expanded={expanded} classNames={{ head: isAdministrative ? "perm-header-readonly" : undefined }}>
        <div>
            {!hasRolesFeature && <InfoableComponent description={<span>This permission grants access and full privileges to the Resources page, as well as to resource plans. Click <Link target="_blank" href="https://help.ppm.express/people-management-and-permissions/resource-permissions">here</Link> for more information.</span>}>
                <Checkbox
                    disabled={readOnly}
                    label="Manage Resources"
                    indeterminate={props.indeterminate?.(perms.resourceEntity)}
                    checked={perms.resourceEntity !== ResourceEntityAccessLevels.None}
                    onChange={(ev, c) => {
                        const p = c ? ResourceEntityAccessLevels.All : ResourceEntityAccessLevels.None;
                        _onCheckChange({ resourceEntity: p, resourcePlan: p });
                    }} />
                </InfoableComponent>}
            {hasRolesFeature && <div>
                <ResourceEntityAccessLevel
                    readOnly={readOnly}
                    description={<span>This permission grants access and full privileges to the Resources page, as well as to resource plans. Click <Link target="_blank" href="https://help.ppm.express/people-management-and-permissions/resource-permissions">here</Link> for more information.</span>}
                    label="Manage Resources"
                    assignedDescription="This permission grants access to the Resources page and to resource plans, but with a reduced scope – only resources where the user is listed as the Direct Manager or Resource Manager will be available."
                    indeterminate={() => props.indeterminate?.(perms.resourceEntity)}
                    perms={perms.resourceEntity}
                    onChange={_ => _onCheckChange({ resourceEntity: _ })}
                />
                <ResourceEntityAccessLevel
                    readOnly={readOnly || perms.resourceEntity === ResourceEntityAccessLevels.All}
                    description="This permission grants privileges to work with resource plans in Projects / Portfolios / Programs and Ideas (depending on which level Resource Planning is performed)."
                    label="Manage Resource Planning"
                    assignedDescription="This permission grants privileges to work with resource plans in Projects / Portfolios / Programs and Ideas (depending on which level Resource Planning is performed), but with a reduced scope – only resources where the user is listed as the Allocation Manager will be available."
                    indeterminate={() => props.indeterminate?.(perms.resourcePlan)}
                    perms={perms.resourcePlan}
                    onChange={_ => _onCheckChange({ resourcePlan: _ })}
                />
            </div>}
        </div>
    </Collapsible>;
}

function mapStateToProps(state: ApplicationState, ownProps?: OwnProps): StateProps {
    return {
        subscription: state.tenant.subscription
    }
}

export default connect(mapStateToProps, {})(ResourceEntityPermssions);